import { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import {
  buildDemoListingDetail,
  demoListingHeadline,
  demoListingImeiDisplay,
  isPlatformVerifiedListing,
  formatListingPublishedAt,
  PAYMENTS_ENABLED,
  type DemoListing,
} from '@redevicer/shared'
import TrustReportPanel from './TrustReportPanel'
import SellerBadge from './SellerBadge'
import BatteryTransparencyPanel from './BatteryTransparencyPanel'
import PartsAuthenticityPanel from './PartsAuthenticityPanel'
import PriceInsightBadge from './PriceInsightBadge'
import ModalCloseButton from './ModalCloseButton'
import ListingNoPhotoPlaceholder from './ListingNoPhotoPlaceholder'
import { inBoxLabelKey } from '../utils/inBoxCompleteness'

type Props = {
  listing: DemoListing | null
  onClose: () => void
  onOpenCatalog?: () => void
}

export default function ExampleListingDetailModal({ listing, onClose, onOpenCatalog }: Props) {
  const { t, i18n } = useTranslation()
  const [closing, setClosing] = useState(false)

  const detail = useMemo(() => (listing ? buildDemoListingDetail(listing) : null), [listing])
  const headline = useMemo(() => (detail ? demoListingHeadline(detail) : null), [detail])

  useEffect(() => {
    if (!listing) return
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') requestClose()
    }
    document.addEventListener('keydown', onKey)
    document.documentElement.classList.add('listing-detail-scroll-lock')
    return () => {
      document.removeEventListener('keydown', onKey)
      document.documentElement.classList.remove('listing-detail-scroll-lock')
    }
  }, [listing])

  function requestClose() {
    setClosing(true)
    window.setTimeout(() => {
      setClosing(false)
      onClose()
    }, 320)
  }

  if (!listing || !detail || !headline) return null

  return (
    <div
      className={`listing-detail-overlay listing-detail-overlay--app example-detail-overlay${closing ? ' listing-detail-overlay--closing' : ''}`}
      role="presentation"
      onClick={requestClose}
    >
      <div
        className={`listing-detail-modal listing-detail-modal--app example-detail-modal${closing ? ' listing-detail-modal--closing' : ''}`}
        role="dialog"
        aria-modal="true"
        aria-labelledby="listing-detail-title"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="listing-detail-modal__glow" aria-hidden="true" />
        <ModalCloseButton
          className="listing-detail-close"
          onClick={requestClose}
          aria-label={t('listing.closeGallery')}
        />

        <div className="example-detail-banner" role="note">
          <span className="example-detail-banner__ribbon">{t('examples.ribbon')}</span>
          <p>{t('examples.detailBanner')}</p>
        </div>

        <div className="listing-detail-layout">
          <div className="listing-detail-col listing-detail-col--media">
            <div className="listing-detail-gallery">
              <div className="listing-detail-main-photo">
                <div className="listing-detail-no-photo-wrap">
                  <ListingNoPhotoPlaceholder variant="detail" labelKey="examples.photoLabel" />
                </div>
              </div>
            </div>

            <div className="listing-detail-actions listing-detail-actions--primary listing-detail-actions--simple">
              <div className="listing-detail-actions-cta">
                <SellerBadge seller={detail.seller} compact className="listing-detail-actions-seller" />
                <div className="listing-detail-action-bar listing-detail-action-bar--demo" role="group" aria-disabled="true">
                  <button type="button" className="listing-detail-action-bar__btn listing-detail-action-bar__btn--primary" disabled>
                    {t('purchase.contactSeller')}
                  </button>
                  <button type="button" className="listing-detail-action-bar__btn" disabled>
                    {t('catalog.offerShort')}
                  </button>
                  {PAYMENTS_ENABLED ? (
                    <button type="button" className="listing-detail-action-bar__btn" disabled>
                      {t('catalog.exchange')}
                    </button>
                  ) : null}
                </div>
                <p className="example-detail-disabled">{t('examples.ctaDisabled')}</p>
                {onOpenCatalog ? (
                  <button type="button" className="examples-primary-btn example-detail-catalog-btn" onClick={onOpenCatalog}>
                    {t('examples.openCatalog')}
                  </button>
                ) : null}
              </div>

              <section className="listing-detail-comments" aria-labelledby="demo-listing-comments-heading">
                <header className="listing-detail-comments__head">
                  <h3 id="demo-listing-comments-heading" className="listing-detail-comments__title">
                    {t('listing.comments.title')}
                  </h3>
                </header>
                <div className="listing-detail-comments__empty-card">
                  <span className="listing-detail-comments__empty-icon" aria-hidden="true">
                    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path
                        d="M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.38 8.38 0 0 1 3.8-.9h.5a8.48 8.48 0 0 1 8 8v.5Z"
                        stroke="currentColor"
                        strokeWidth="1.8"
                        strokeLinejoin="round"
                      />
                    </svg>
                  </span>
                  <p className="listing-detail-comments__empty-title">{t('listing.comments.emptyTitle')}</p>
                  <p className="listing-detail-comments__empty-hint">{t('listing.comments.emptyHint')}</p>
                </div>
              </section>
            </div>
          </div>

          <div className="listing-detail-col listing-detail-col--info">
            <div className="listing-detail-intro">
              <p className="listing-detail-brand">{headline.brand}</p>
              <div className="listing-detail-title-row">
                <h2 id="listing-detail-title" className="listing-detail-title">
                  {headline.title}
                </h2>
                <div className="listing-detail-title-actions listing-detail-title-actions--demo">
                  <button type="button" className="listing-detail-share-btn" disabled aria-disabled="true">
                    {t('listing.share')}
                  </button>
                </div>
              </div>
              <div className="listing-detail-price-row">
                <p className="listing-detail-price">€{detail.price.toFixed(2)}</p>
                <PriceInsightBadge listing={detail} variant="detail" showMarket />
              </div>
            </div>

            <div className="listing-detail-body">
              <div className="listing-detail-main-info-grid">
                <p
                  className={`listing-detail-declared-kicker seller-declared-kicker${
                    isPlatformVerifiedListing(detail) ? ' seller-declared-kicker--verified' : ''
                  }`}
                >
                  {t(
                    isPlatformVerifiedListing(detail)
                      ? 'trust.report.verifiedBadge'
                      : 'batteryTransparency.kicker',
                  )}
                </p>
                <BatteryTransparencyPanel
                  showKicker={false}
                  listing={{
                    battery_health: detail.battery_health,
                    battery_cycles: detail.battery_cycles,
                    battery_original: detail.battery_original,
                    battery_tested_at: detail.battery_tested_at,
                    manufacturer: detail.phone_model.manufacturer,
                  }}
                />
                <PartsAuthenticityPanel
                  showKicker={false}
                  parts={detail.parts_authenticity}
                  batteryOriginal={detail.battery_original}
                  manufacturer={detail.phone_model.manufacturer}
                  modelName={detail.phone_model.model_name}
                />
              </div>

              <TrustReportPanel listing={detail} loggedIn={false} />

              <div className="listing-detail-metadata-row">
                <section className="listing-detail-spec-summary" aria-label={t('listing.detailDeviceInfo')}>
                  <dl className="listing-detail-specs">
                    {detail.created_at ? (
                      <div>
                        <dt>{t('listing.publishedAt')}</dt>
                        <dd>
                          <time dateTime={detail.created_at}>
                            {formatListingPublishedAt(detail.created_at, i18n.language)}
                          </time>
                        </dd>
                      </div>
                    ) : null}
                    <div>
                      <dt>{t('listing.detailCondition')}</dt>
                      <dd>{t(`listing.condition.${detail.condition}`, detail.condition)}</dd>
                    </div>
                    {detail.in_box_completeness ? (
                      <div>
                        <dt>{t('listing.detailKit')}</dt>
                        <dd>{t(inBoxLabelKey(detail.in_box_completeness))}</dd>
                      </div>
                    ) : null}
                    <div>
                      <dt>{t('listing.warrantyLegend')}</dt>
                      <dd>
                        {detail.has_factory_warranty
                          ? detail.warranty_remaining
                            ? t('listing.warrantyRemainingBadge', {
                                left: t(
                                  `listing.warrantyRemaining.${detail.warranty_remaining}`,
                                  detail.warranty_remaining,
                                ),
                              })
                            : t('listing.warrantyBadgeYes')
                          : t('listing.warrantyBadgeNo')}
                      </dd>
                    </div>
                    {detail.phone_model.release_year != null ? (
                      <div>
                        <dt>{t('listing.releaseYear')}</dt>
                        <dd>{detail.phone_model.release_year}</dd>
                      </div>
                    ) : null}
                    {detail.imei ? (
                      <div>
                        <dt>{t('listing.detailImei')}</dt>
                        <dd>{demoListingImeiDisplay(listing.imeiMasked)}</dd>
                      </div>
                    ) : null}
                    {detail.seller_city ? (
                      <div>
                        <dt>{t('listing.sellerCityLabel')}</dt>
                        <dd>{detail.seller_city}</dd>
                      </div>
                    ) : null}
                  </dl>
                </section>
                <p className="listing-detail-no-guarantee">{t('listing.noGuarantee')}</p>
              </div>

              {detail.description?.trim() ? (
                <section className="listing-detail-desc listing-detail-desc--top">
                  <h3>{t('listing.detailAbout')}</h3>
                  <p>{detail.description}</p>
                </section>
              ) : null}
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
